<svelte:options
  customElement={{
    tag: "oc-button-v1",
    shadow: "none",
    /*                                                         */
    /*         */
    extend: (customElementConstructor) => {
      /*                                                           */
      return class extends customElementConstructor {
        static formAssociated = true;

        constructor() {
          super();
          this.attachShadow({ mode: "open", delegatesFocus: true });
          this.internals = this.attachInternals();
        }

        get form() {
          return this.internals.form;
        }
      };
    },
    props: {
      aria: { type: "Object" },
      variant: { type: "String", reflect: true },
      size: { type: "String", reflect: true },
      iconTypeLeft: { type: "String", attribute: "icon-type-left" },
      iconTypeRight: { type: "String", attribute: "icon-type-right" },
      fitContent: { type: "Boolean", attribute: "fit-content", reflect: true },
      disabled: { type: "Boolean", reflect: true },
      type: { type: "String" },
    },
  }}
/>

<script lang="ts">
  import type { Props } from "./ButtonV1.types";
  import type { CommonProps } from "../../../common/component.types.js";
  import { useAriaAttributes } from "../../../common/utils/aria/useAriaAttributes.js";
  import { applyAttributesToHost } from "../../../common/utils/aria/applyAttributesToHost.js";

  export let aria: Required<CommonProps>["aria"] = {};
  $: ariaAttributes = useAriaAttributes(aria, { host: ["hidden"] });
  $: applyAttributesToHost(ariaAttributes.host);

  export let variant: Props["variant"] = "primary";
  export let size: Props["size"] = "100";
  export let iconTypeLeft: Props["iconTypeLeft"] = undefined;
  export let iconTypeRight: Props["iconTypeRight"] = undefined;
  export let fitContent: Props["fitContent"] = false;
  export let disabled: Props["disabled"] = false;

  export let type: Props["type"] = undefined;

  export let internals: ElementInternals;

  const handleClick = () => {
    if (type === "submit") {
      if (internals.form?.checkValidity()) {
        internals.form.requestSubmit();
      } else if (internals.form?.reportValidity) {
        internals.form.reportValidity();
      }
    } else if (type === "reset") {
      internals.form?.reset();
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === "Space") {
      event.preventDefault();
      handleClick();
    }
  };
</script>

<button
  class="button button--variant-{variant} button--size-{size}"
  class:button--fit-content={fitContent}
  class:button--has-icon-left={iconTypeLeft}
  class:button--has-icon-right={iconTypeRight}
  {disabled}
  on:click={handleClick}
  on:keydown={handleKeyDown}
  {type}
  {...ariaAttributes.default}
>
  {#if iconTypeLeft}
    <oc-icon-v1 class="button__icon button__icon--left" type={iconTypeLeft} {size} />
  {/if}

  <span>
    <slot />
  </span>

  {#if iconTypeRight}
    <oc-icon-v1 class="button__icon button__icon--right" type={iconTypeRight} {size} />
  {/if}
</button>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";
  @use "./ButtonV1.config" as config;
  @use "../../../common/scss/mixins/mixins.scss";

  $oc-component-button-100-span-spacing: calc(
    tokens.$oc-component-button-100-icon-size + tokens.$oc-component-button-100-padding-x-icon
  );
  $oc-component-button-50-span-spacing: calc(
    tokens.$oc-component-button-50-icon-size + tokens.$oc-component-button-50-padding-x-icon
  );

  :host {
    @include config.oc-button;
  }

  :host([size="50"]) {
    @include config.oc-button-size-50;
  }

  :host([size="100"]) {
    @include config.oc-button-size-100;
  }

  :host([fit-content]) {
    @include config.oc-button-fit-content;
  }

  /*                                   */
  .button {
    align-items: center;
    border-radius: tokens.$oc-component-button-100-border-radius;
    border: none;
    box-sizing: border-box;
    display: inline-flex;
    font: tokens.$oc-component-button-100-primary-font;
    height: 48px;
    justify-content: center;
    margin: 0;
    outline: 0 none;
    padding: tokens.$oc-component-button-100-padding-y
      tokens.$oc-component-button-100-padding-x-default;
    position: relative;
    text-decoration: none;
    width: 100%;
    -webkit-tap-highlight-color: transparent;

    &--has-icon-left,
    &--has-icon-right {
      padding: tokens.$oc-component-button-100-padding-y
        tokens.$oc-component-button-100-padding-x-icon;

      > span {
        padding: 0 $oc-component-button-100-span-spacing;
      }
    }

    &--fit-content {
      width: fit-content;

      &.button--has-icon-left {
        > span {
          padding: 0 tokens.$oc-component-button-100-padding-x-icon 0
            $oc-component-button-100-span-spacing;
        }
      }

      &.button--has-icon-right {
        > span {
          padding: 0 $oc-component-button-100-span-spacing 0
            tokens.$oc-component-button-100-padding-x-icon;
        }
      }

      &.button--has-icon-left.button--has-icon-right {
        > span {
          padding: 0 $oc-component-button-100-span-spacing;
        }
      }
    }

    > .button__icon {
      height: tokens.$oc-component-button-100-icon-size;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: tokens.$oc-component-button-100-icon-size;

      &--left {
        left: tokens.$oc-component-button-100-padding-x-icon;
      }

      &--right {
        left: auto;
        right: tokens.$oc-component-button-100-padding-x-icon;
      }
    }

    > span {
      overflow: hidden;
      text-overflow: ellipsis;
      user-select: none;
      white-space: nowrap;
    }

    &:focus:enabled {
      @include mixins.focus-styles(tokens.$oc-component-button-100-focus-outline-radius);
    }

    &--variant-primary {
      background-color: tokens.$oc-component-button-primary-background-color;
      color: tokens.$oc-component-button-primary-text-color;

      > .button__icon {
        fill: tokens.$oc-component-button-primary-icon-color;
      }

      @media (hover: hover) {
        &:hover:enabled {
          background-color: tokens.$oc-component-button-primary-background-color-hover;
          cursor: pointer;
        }
      }

      &:active:enabled {
        background-color: tokens.$oc-component-button-primary-background-color-active;
        cursor: pointer;
      }
    }

    &--variant-secondary {
      background-color: tokens.$oc-component-button-secondary-background-color;
      color: tokens.$oc-component-button-secondary-text-color;
      font: tokens.$oc-component-button-100-secondary-font;

      > .button__icon {
        fill: tokens.$oc-component-button-secondary-icon-color;
      }

      @media (hover: hover) {
        &:hover:enabled {
          background-color: tokens.$oc-component-button-secondary-background-color-hover;
          cursor: pointer;
        }
      }

      &:active:enabled {
        background-color: tokens.$oc-component-button-secondary-background-color-active;
        cursor: pointer;
      }
    }

    &--variant-tertiary {
      background-color: tokens.$oc-component-button-tertiary-background-color;
      color: tokens.$oc-component-button-tertiary-text-color;
      font: tokens.$oc-component-button-100-tertiary-font;

      > .button__icon {
        fill: tokens.$oc-component-button-tertiary-icon-color;
      }

      @media (hover: hover) {
        &:hover:enabled {
          background-color: tokens.$oc-component-button-tertiary-background-color-hover;
          cursor: pointer;
        }
      }

      &:active:enabled {
        background-color: tokens.$oc-component-button-tertiary-background-color-active;
        cursor: pointer;
      }
    }

    &:disabled {
      background-color: tokens.$oc-component-button-disabled-background-color;
      color: tokens.$oc-component-button-disabled-text-color;
      font: tokens.$oc-component-button-100-disabled-font;

      > .button__icon {
        fill: tokens.$oc-component-button-disabled-icon-color;
      }
    }

    &--size-50 {
      @include mixins.focus-styles(tokens.$oc-component-button-50-focus-outline-radius);

      border-radius: tokens.$oc-component-button-50-border-radius;
      height: 32px;
      padding: tokens.$oc-component-button-50-padding-y
        tokens.$oc-component-button-50-padding-x-default;
      width: auto;

      &.button--has-icon-left {
        padding-top: tokens.$oc-component-button-50-padding-y;
        padding-right: tokens.$oc-component-button-50-padding-x-default;
        padding-bottom: tokens.$oc-component-button-50-padding-y;
        padding-left: calc(tokens.$oc-component-button-50-padding-x-default / 2);

        > span {
          padding: 0 0 0 $oc-component-button-50-span-spacing;
        }
      }

      &.button--has-icon-right {
        padding-top: tokens.$oc-component-button-50-padding-y;
        padding-right: calc(tokens.$oc-component-button-50-padding-x-default / 2);
        padding-bottom: tokens.$oc-component-button-50-padding-y;
        padding-left: tokens.$oc-component-button-50-padding-x-default;

        > span {
          padding: 0 $oc-component-button-50-span-spacing 0 0;
        }
      }

      &.button--has-icon-left.button--has-icon-right {
        padding: tokens.$oc-component-button-50-padding-y
          calc(tokens.$oc-component-button-50-padding-x-default / 2);

        > span {
          padding: 0 $oc-component-button-50-span-spacing;
        }
      }

      > .button__icon {
        height: tokens.$oc-component-button-50-icon-size;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: tokens.$oc-component-button-50-icon-size;

        &--left {
          left: tokens.$oc-component-button-50-padding-x-icon;
        }

        &--right {
          left: auto;
          right: tokens.$oc-component-button-50-padding-x-icon;
        }
      }

      &.button--variant-primary {
        font: tokens.$oc-component-button-50-primary-font;
      }

      &.button--variant-secondary {
        font: tokens.$oc-component-button-50-secondary-font;
      }

      &.button--variant-tertiary {
        font: tokens.$oc-component-button-50-tertiary-font;
      }

      &:disabled {
        font: tokens.$oc-component-button-50-disabled-font;
      }
    }
  }
</style>
